import { StyleSheet, View } from 'react-native'
import { Button, Card, Chip, Divider, Text, useTheme } from 'react-native-paper'
import { formatPrice, isUnlimited } from '@/lib/billing'
import type { OrgUsage, PlanLimits } from '@/types/billing'

interface PlanOption {
    id: string
    name: string
    description: string
    price_monthly: number
    limits: PlanLimits
    features: string[]
}

interface PricingTableProps {
    currentPlanId: string
    usage?: OrgUsage
    onSelectPlan: (planId: string) => void
}

const plans: PlanOption[] = [
    {
        id: 'free',
        name: '3 for Free',
        description: 'For small teams getting started',
        price_monthly: 0,
        limits: {
            active_team_members: 3,
            active_jobs: 3,
            active_customers: 3,
        },
        features: ['Basic time tracking', 'Manual time entries'],
    },
    {
        id: 'startup',
        name: 'Startup',
        description: 'For small teams ready to grow',
        price_monthly: 1900,
        limits: {
            active_team_members: 5,
            active_jobs: 10,
            active_customers: 10,
        },
        features: ['Everything in Free', 'Geofence tracking', 'Real-time monitoring'],
    },
    {
        id: 'pro',
        name: 'Pro',
        description: 'For growing teams',
        price_monthly: 4900,
        limits: {
            active_team_members: 25,
            active_jobs: 100,
            active_customers: 100,
        },
        features: ['Everything in Startup', 'Approval workflows', 'Advanced reports'],
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        description: 'For large organizations',
        price_monthly: 19900,
        limits: {
            active_team_members: -1,
            active_jobs: -1,
            active_customers: -1,
        },
        features: [
            'Everything in Pro',
            'Unlimited everything',
            'Priority support',
            'Custom integrations',
        ],
    },
]

function getRecommendedPlanId(currentPlanId: string, usage?: OrgUsage) {
    if (!usage) return undefined

    const fitsUsage = (limits: PlanLimits) =>
        (isUnlimited(limits.active_team_members) ||
            limits.active_team_members > usage.active_team_members) &&
        (isUnlimited(limits.active_jobs) || limits.active_jobs > usage.active_jobs) &&
        (isUnlimited(limits.active_customers) || limits.active_customers > usage.active_customers)

    for (const plan of plans) {
        if (plan.id === currentPlanId) continue
        if (fitsUsage(plan.limits)) return plan.id
    }
    return undefined
}

export function PricingTable({ currentPlanId, usage, onSelectPlan }: PricingTableProps) {
    const theme = useTheme()
    const recommendedId = getRecommendedPlanId(currentPlanId, usage)

    const formatLimit = (limit: number) => {
        if (isUnlimited(limit)) return 'Unlimited'
        return limit.toString()
    }

    return (
        <View style={styles.container}>
            {plans
                .filter(plan => plan.id !== currentPlanId)
                .map(plan => {
                    const isUpgrade =
                        plans.findIndex(p => p.id === plan.id) >
                        plans.findIndex(p => p.id === currentPlanId)
                    const isRecommended = plan.id === recommendedId

                    return (
                        <Card
                            key={plan.id}
                            style={[
                                styles.card,
                                isRecommended && {
                                    borderColor: theme.colors.primary,
                                    borderWidth: 2,
                                },
                            ]}
                        >
                            <Card.Content>
                                <View style={styles.header}>
                                    <Text variant="titleLarge" style={styles.name}>
                                        {plan.name}
                                    </Text>
                                    {isRecommended && (
                                        <Chip
                                            compact
                                            mode="flat"
                                            style={{
                                                backgroundColor: theme.colors.primary,
                                            }}
                                            textStyle={{
                                                color: theme.colors.onPrimary,
                                            }}
                                        >
                                            Recommended
                                        </Chip>
                                    )}
                                </View>

                                <Text
                                    variant="bodyMedium"
                                    style={{
                                        color: theme.colors.onSurfaceVariant,
                                        marginBottom: 16,
                                    }}
                                >
                                    {plan.description}
                                </Text>

                                <View style={styles.priceRow}>
                                    {plan.price_monthly > 0 ? (
                                        <>
                                            <Text variant="headlineMedium" style={styles.price}>
                                                {formatPrice(plan.price_monthly)}
                                            </Text>
                                            <Text
                                                variant="bodyMedium"
                                                style={{
                                                    color: theme.colors.onSurfaceVariant,
                                                }}
                                            >
                                                /month
                                            </Text>
                                        </>
                                    ) : (
                                        <Text variant="headlineMedium" style={styles.price}>
                                            Free
                                        </Text>
                                    )}
                                </View>

                                <Divider style={styles.divider} />

                                <View style={styles.limits}>
                                    <Text variant="labelMedium" style={styles.limitLabel}>
                                        {formatLimit(plan.limits.active_team_members)} team members
                                    </Text>
                                    <Text variant="labelMedium" style={styles.limitLabel}>
                                        {formatLimit(plan.limits.active_jobs)} active jobs
                                    </Text>
                                    <Text variant="labelMedium" style={styles.limitLabel}>
                                        {formatLimit(plan.limits.active_customers)} customers
                                    </Text>
                                </View>

                                <Divider style={styles.divider} />

                                <View style={styles.features}>
                                    {plan.features.map(feature => (
                                        <View key={feature} style={styles.featureRow}>
                                            <Text style={styles.checkmark}>{'\u2713'}</Text>
                                            <Text variant="bodySmall" style={styles.featureText}>
                                                {feature}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            </Card.Content>

                            <Card.Actions style={styles.actions}>
                                {isUpgrade ? (
                                    <Button mode="contained" onPress={() => onSelectPlan(plan.id)}>
                                        Upgrade
                                    </Button>
                                ) : (
                                    <Button mode="outlined" onPress={() => onSelectPlan(plan.id)}>
                                        Downgrade
                                    </Button>
                                )}
                            </Card.Actions>
                        </Card>
                    )
                })}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        gap: 16,
    },
    card: {
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    name: {
        fontWeight: 'bold',
    },
    priceRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 4,
    },
    price: {
        fontWeight: 'bold',
    },
    divider: {
        marginVertical: 16,
    },
    limits: {
        gap: 4,
    },
    limitLabel: {
        fontWeight: '500',
    },
    features: {
        gap: 8,
    },
    featureRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    checkmark: {
        color: '#22c55e',
        fontWeight: 'bold',
    },
    featureText: {
        flex: 1,
    },
    actions: {
        paddingHorizontal: 16,
        paddingBottom: 16,
    },
})
